/**
 * PdfViewerModal Component
 * A modal for viewing and downloading PDF documents
 * Tailwind version using OathUI components
 */

import { memo, useMemo } from 'react';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { OathButton, OathSpinner } from '../../../shared/OathUI';
import { toast } from 'react-toastify';

/**
 * PdfViewerModal Component
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {string} pdfData - Base64 encoded PDF data
 * @param {string} reportNumber - Report number for display and filename
 * @param {boolean} isLoading - Whether PDF is currently loading
 * @param {string} title - Optional custom title (defaults to "Report: {reportNumber}")
 */
const isMobileDevice = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const PdfViewerModal = ({
  isOpen,
  onClose,
  pdfData = null,
  reportNumber = '',
  isLoading = false,
  title = null
}) => {
  const isMobile = useMemo(() => isMobileDevice(), []);

  // Convert base64 to blob URL for mobile "open in browser"
  const blobUrl = useMemo(() => {
    if (!pdfData) return null;
    try {
      const byteCharacters = atob(pdfData);
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteNumbers], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, [pdfData]);

  // Open PDF in a new browser tab (mobile native viewer)
  const handleOpenInBrowser = () => {
    if (blobUrl) window.open(blobUrl, '_blank');
  };

  // Handle PDF download
  const handleDownload = () => {
    if (!pdfData) return;

    try {
      // Convert base64 to blob
      const byteCharacters = atob(pdfData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PowerReport_${reportNumber || 'Report'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully');
    } catch (err) {
      toast.error('Failed to download PDF');
      console.error('PDF download error:', err);
    }
  };

  const displayTitle = title || `Report: ${reportNumber}`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 sm:inset-4 md:inset-8 flex flex-col">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-red-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {displayTitle}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <OathButton
                onClick={handleDownload}
                disabled={!pdfData || isLoading}
                icon={ArrowDownTrayIcon}
                size="sm"
              >
                <span className="hidden sm:inline">Download</span>
              </OathButton>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full gap-4">
                <OathSpinner size="lg" />
                <p className="text-gray-500 dark:text-gray-400">Loading PDF...</p>
              </div>
            ) : pdfData ? (
              isMobile ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 px-6">
                  <DocumentTextIcon className="h-20 w-20 text-gray-300 dark:text-gray-600" />
                  <p className="text-gray-700 dark:text-gray-300 font-medium text-center">
                    PDF preview is not supported on mobile devices
                  </p>
                  <div className="flex flex-col gap-3 w-full max-w-xs">
                    <button
                      onClick={handleOpenInBrowser}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                      Open in Browser
                    </button>
                    <button
                      onClick={handleDownload}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                      Download PDF
                    </button>
                  </div>
                </div>
              ) : (
                <iframe
                  src={`data:application/pdf;base64,${pdfData}`}
                  title={`PDF ${displayTitle}`}
                  className="w-full h-full border-none"
                />
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-2">
                <DocumentTextIcon className="h-16 w-16 text-gray-300 dark:text-gray-600" />
                <p className="text-red-500 dark:text-red-400 font-medium">
                  Failed to load PDF
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  The PDF document could not be loaded.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(PdfViewerModal);
